import { Map as immutableMap } from 'immutable';
import { getField, updateField } from 'vuex-map-fields';
import objectValue from '@/utils/objectValue';
import sleep from '@/utils/sleep';
import groupBy from '@/utils/groupBy';

const STATE = immutableMap({
  positionsUpdatedAt: Date.now(),
  itemsPositions: {},
});

export const state = () => STATE.toJS();

export const getters = {
  getField,
};

export const mutations = {
  reset(state) {
    Object.assign(state, {
      ...STATE.toJS(),
      positionsUpdatedAt: Date.now(),
    });
  },
  updateField,
};

export const actions = {
  /**
   * Reposition children
   */
  async repositionChildren(context) {
    const items = objectValue(context, 'rootState.nodes.items');
    if (!Array.isArray(items)) {
      return false;
    }

    context.commit('updateField', {
      path: 'itemsPositions',
      value: STATE.toJS().itemsPositions,
    });

    await sleep(1);

    let itemsPositions = {};

    const setPosition = fromContainerElement => {
      const containerId = fromContainerElement.dataset.positionFromContainer;
      const toContainerElement = document.querySelector(`[data-position-to-container=${containerId}]`);
      if (!toContainerElement) {
        return false;
      }

      const fromContainerH = fromContainerElement && fromContainerElement.getBoundingClientRect().height;
      const toContainerH = toContainerElement && toContainerElement.getBoundingClientRect().height;

      if (fromContainerH <= toContainerH) {
        return false;
      }

      let toItemElements = toContainerElement
        .querySelectorAll(`[data-position-to][data-position-container-id=${containerId}]`);

      for (const [index, toItemElement] of Object.entries(toItemElements)) {
        const id = toItemElement.dataset.positionTo;
        const item = items.find(i => i.id === id);
        if (!item) {
          return false;
        }
        const fromId = item.from;

        const fromItemElement = fromContainerElement
          .querySelector(`[data-position-from=${fromId}]`);
        if (!fromItemElement) {
          return false;
        }

        const fromItemTop = fromItemElement.getBoundingClientRect().top;
        const fromItemH = fromItemElement.getBoundingClientRect().height;
        const fromItemCenter = fromItemTop + fromItemH / 2;

        const toItemTop = toItemElement.getBoundingClientRect().top;
        const toItemH = toItemElement.getBoundingClientRect().height;
        const toItemCenter = toItemTop + toItemH / 2;

        const adjustY = fromItemCenter - toItemCenter;

        const toItemOuterElement = toItemElement.closest(`[data-position-to-outer=${id}]`);
        if (!toItemOuterElement) {
          return false;
        }

        const toItemOuterTop = toItemOuterElement.getBoundingClientRect().top;
        const toItemOuterH = toItemOuterElement.getBoundingClientRect().height;
        const toItemOuterTopAdjusted = toItemOuterTop + adjustY;
        const toItemOuterBottomAdjusted = toItemOuterTop + toItemOuterH + adjustY;

        let addTop = 0;
        if (index > 0) {
          const prevToItemElement = toItemElements[index - 1];
          const prevToItemId = prevToItemElement.dataset.positionTo;
          const prevItemPosition = itemsPositions[prevToItemId];
          const prevItemAlignedBottom = prevItemPosition.toItemOuterBottomAdjusted + prevItemPosition.addTop;
          if (prevItemAlignedBottom > toItemOuterTopAdjusted) {
            addTop = prevItemAlignedBottom - toItemOuterTopAdjusted;
          }
        }

        itemsPositions[id] = {
          id,
          fromId,
          containerId,
          adjustY,
          toItemOuterBottomAdjusted,
          addTop,
        };
      }
    }

    const fromContainerElements = document.querySelectorAll('[data-position-from-container]');
    for (const fromContainerElement of fromContainerElements) {
      setPosition(fromContainerElement);
    }

    context.commit('updateField', {
      path: 'itemsPositions',
      value: itemsPositions,
    });

    context.commit('updateField', {
      path: 'positionsUpdatedAt',
      value: Date.now(),
    });

    return true;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
